import { useState } from "react";
import { registry } from "../../registry";
import type { SubscriptionManager } from "../../core/subscriptions";
import { ArgForm, type ArgFormValues } from "../ArgForm";

type Props = {
  manager: SubscriptionManager | undefined;
  /** Bumped by the parent whenever the event log changes, to refresh live delivery counts. */
  version: number;
};

export function SubscriptionsPanel({
  manager,
  version,
}: Props): React.JSX.Element {
  const [argValues, setArgValues] = useState<Record<string, ArgFormValues>>({});

  if (!manager) {
    return (
      <p className="empty-state">
        Not connected yet — subscriptions are unavailable.
      </p>
    );
  }

  const counts = manager.counts();
  void version; // re-render trigger only; counts() is read fresh above on every render.

  const subscribable = registry.filter(
    (m) => m.kind !== "interceptor" && m.events.length > 0,
  );

  return (
    <div className="subscriptions-panel">
      <div className="subscriptions-actions">
        <button type="button" onClick={() => manager.subscribeAll()}>
          Subscribe all
        </button>
        <button type="button" onClick={() => manager.unsubscribeAll()}>
          Unsubscribe all
        </button>
      </div>

      {subscribable.map((mod) => (
        <fieldset key={mod.id} className="subscriptions-module">
          <legend>{mod.label}</legend>
          {mod.events.map((e) => {
            const key = `${mod.id}:${e.name}`;
            const active = manager.isActive(mod.id, e.name);
            const requiredArgs = (e.args ?? []).filter((a) => a.required);
            const values = argValues[key] ?? {};
            return (
              <div className="subscription-row" key={e.name}>
                <label className={e.deprecated ? "deprecated" : undefined}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(ev) => {
                      if (ev.target.checked) {
                        manager.subscribe(mod.id, e.name, values);
                      } else {
                        manager.unsubscribe(mod.id, e.name);
                      }
                    }}
                  />
                  {e.name}
                  {e.deprecated ? (
                    <span className="deprecated-tag">deprecated</span>
                  ) : null}
                </label>
                <span className="subscription-count">
                  {counts[key] ?? 0} delivered
                </span>
                {requiredArgs.length > 0 ? (
                  <div className="subscription-args">
                    <p className="subscription-args-hint">
                      Requires {requiredArgs.map((a) => a.k).join(", ")} — the
                      subscription CANNOT fire without it.
                    </p>
                    <ArgForm
                      spec={e.args ?? []}
                      values={values}
                      onChange={(v) =>
                        setArgValues((prev) => ({ ...prev, [key]: v }))
                      }
                      idPrefix={key}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}
